import { useLocation } from 'react-router-dom';
import { Briefcase, Heart } from 'lucide-react';
import useUserStore from '../../stores/useUserStore';
import { useWorkLifeFilter } from '../../contexts/WorkLifeFilterContext';
import ContextSwitcher from './ContextSwitcher';
import HeaderActions from './HeaderActions';
import WindowControls from './WindowControls';
import { isTauri } from '../../utils/desktopWindow';
import FocusModeTabs from '../Focus/FocusModeTabs';
import { isFocusPath } from '../Focus/focusModes';
import './Header.css';

/**
 * Header — the thin utility bar above the routed page.
 *
 * WHAT IT IS NOT ANY MORE. The sidebar rebuild moved the brand lockup, the
 * search trigger and the whole profile row into the sidebar, so the copies that
 * used to live up here were duplicate chrome. Both are gone:
 *   · search        → sidebar header (still dispatches the same ⌘K event).
 *   · profile menu  → sidebar footer profile row (navigates to /settings).
 * The dropdown's theme switch, Sign Out and the streak / level / XP readout had
 * no sidebar equivalent, so each was checked for another home before the panel
 * went: theme → Settings > Preferences (PreferencesTab theme picker), Sign Out →
 * Settings > Profile (ProfileTab), streak / level / total XP → /gamification,
 * which renders all three. Nothing here was the last copy of a feature.
 *
 * The sidebar collapse button was retired here earlier (decision 6): it rendered
 * AppShell's raw preference while the Sidebar rendered a session-derived value,
 * so mid-session it offered "Collapse" over an already collapsed rail.
 *
 * AUTO-HIDE IS GONE TOO. It translated the bar off-screen on a mouse-idle timer
 * but left its 48px row in the flex column, i.e. exactly the "empty bar with
 * residual height" this pass is meant to kill. A bar that renders `null` when it
 * has nothing to say is the honest version of the same idea.
 *
 * WHAT RENDERS: the Focus mode tabs (only on the Focus page's own routes — see
 * components/Focus/focusModes.js), the context filter (only while the
 * `contextFilter` module is on), plus the two utility entry points, Inbox and AI
 * Coach (see HeaderActions.jsx — MobileHeader renders the identical pair
 * through its `actions` slot, so a phone gets the same two buttons). When
 * there is no content for the current screen the component returns `null` —
 * no element, no border, no reserved height. AppShell drives that via
 * `hidden`, which is true for the duration of a focus session (same contract
 * MobileHeader already uses), so the tabs correctly vanish for a running session
 * and return when it ends.
 */
export default function Header({ hidden = false }) {
    const { pathname } = useLocation();

    // ─── Context Filters module ───
    // Off (the default) ⇒ the control does not exist. AppShell separately
    // guarantees the FILTER resolves to 'all' in that case, so a stale persisted
    // 'work'/'life' can never silently hide data behind a missing control.
    const contextFilterOn = useUserStore(s => s.isModuleEnabled('contextFilter'));
    const { filter, setFilter } = useWorkLifeFilter();

    // Clicking the active segment returns to 'all' (show everything).
    const handleContextToggle = (mode) => {
        setFilter(filter === mode ? 'all' : mode);
    };

    // Honest name: today this IS a visibility test, not a content test. The bar
    // always has content on desktop (Inbox + AI Coach are unconditional), so the
    // only thing that empties it is being hidden outright. Rename this the day a
    // per-route opt-in lands and the guard starts asking a real question.
    const isVisible = !hidden;

    // ─── On the desktop the bar is also the title bar ───
    // `decorations: false` (tauri.windows/linux.conf.json) means there is no OS
    // bar behind this one: this element IS the drag region and it carries the
    // window buttons. So it must survive `hidden` — returning null mid-session
    // would leave the window with no way to be moved, minimised or closed until
    // the session ended. Its CONTENT still hides, which is what `hidden` was
    // ever really asking for, and the height it keeps is the height the OS bar
    // used to occupy anyway. On the web, `hidden` still means gone.
    const isTitleBar = isTauri();
    if (!isVisible && !isTitleBar) return null;

    const chromeOnly = !isVisible;

    return (
        <header
            className={`header${chromeOnly ? ' header--chrome-only' : ''}`}
            {...(isTitleBar ? { 'data-tauri-drag-region': true } : {})}
        >
            {chromeOnly ? <div className="header__lead" /> : (
            <div className="header__lead">
                {isFocusPath(pathname) && <FocusModeTabs />}

                {contextFilterOn && (
                    <div className="header__context-toggle" role="group" aria-label="Context filter">
                        <span
                            aria-hidden="true"
                            className={[
                                'header__context-highlight',
                                filter === 'all' ? 'header__context-highlight--hidden' : '',
                                filter === 'life' ? 'header__context-highlight--life' : '',
                            ].filter(Boolean).join(' ')}
                        />
                        <button
                            type="button"
                            className={`header__context-option ${filter === 'work' ? 'header__context-option--active' : ''}`}
                            onClick={() => handleContextToggle('work')}
                            aria-pressed={filter === 'work'}
                        >
                            <Briefcase size={14} aria-hidden="true" />
                            Work
                        </button>
                        <button
                            type="button"
                            className={`header__context-option ${filter === 'life' ? 'header__context-option--active' : ''}`}
                            onClick={() => handleContextToggle('life')}
                            aria-pressed={filter === 'life'}
                        >
                            <Heart size={14} aria-hidden="true" />
                            Life
                        </button>
                    </div>
                )}

                {/* Second filter dimension, same module. Work/Life filters by
                    area; this filters by project or by any select property —
                    the "different types of filters" the module is growing into.
                    It was orphaned after the sidebar rebuild while its store
                    still filtered four pages, so mounting it here is what makes
                    a persisted context clearable again. */}
                {contextFilterOn && <ContextSwitcher variant="inline" />}
            </div>
            )}

            <div className="header__actions">
                {!chromeOnly && <HeaderActions />}
                <WindowControls />
            </div>
        </header>
    );
}
